import { Moment } from 'moment';
import { toMoment } from '@core/utils/time';
import { HolidaySet, isWorkingDay } from './business_days';

export interface WeekdaySegment {
  start: number;
  end: number;
  /** True for the segment carrying the event's real start — the only one that should be editable */
  isFirst: boolean;
}

/**
 * Splits an event's span into runs of consecutive WORKING days so a business-day event never
 * renders across Sat/Sun or a non-working company holiday (e.g. a duration-4 event starting Friday becomes a Fri bar plus a
 * Mon–Wed bar, with the weekend cells left empty). Display-only — stored dates are untouched.
 *
 * All-day segment ends are bumped past midnight because FullCalendar treats all-day ends as
 * EXCLUSIVE midnights (an end inside the last day would drop that day from the rendered span).
 * An event that only covers weekend days falls back to a single unsplit segment.
 */
export function splitIntoWeekdaySegments(event: { start: number; end: number; allDay: boolean }, holidays: HolidaySet = new Set()): WeekdaySegment[] {
  const startM = toMoment(event.start);
  const endM = toMoment(event.end);

  const runs: { first: Moment; last: Moment }[] = [];
  let run: { first: Moment; last: Moment } | null = null;
  const cursor = startM.clone().startOf('day');
  const lastDay = endM.clone().startOf('day');
  while (cursor.isSameOrBefore(lastDay, 'day')) {
    if (isWorkingDay(cursor.unix(), holidays)) {
      if (run) run.last = cursor.clone();
      else run = { first: cursor.clone(), last: cursor.clone() };
    } else if (run) {
      runs.push(run);
      run = null;
    }
    cursor.add(1, 'day');
  }
  if (run) runs.push(run);
  if (!runs.length) runs.push({ first: startM.clone().startOf('day'), last: endM.clone().startOf('day') });

  return runs.map((r, index) => ({
    isFirst: index === 0,
    // The first segment keeps the real start only when it IS a working day; an event anchored on a
    // Saturday (legacy manual drop) renders from the run's first working day, never across the weekend.
    start: index === 0 && startM.isSame(r.first, 'day') ? event.start : r.first.clone().hour(startM.hour()).minute(startM.minute()).unix(),
    end: event.allDay ? r.last.clone().endOf('day').add(1, 'second').unix() : r.last.clone().hour(endM.hour()).minute(endM.minute()).unix(),
  }));
}
